import { Button, TextField, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flex from "../../../../../../components/Flex";
import StyledDialog from "../../../../../../components/StyledDialog";
import { DisclosureHost } from "../../../../../../hooks/use-disclosure";
import PatientSelectScroll from "@/components/PatientSelectScroll";
// import CustomTextArea from "../../../../../../components/CustomTextArea";
import { AsyncSelectOption } from "../../../../../../components/AsyncSelect";
import { usePatientDocumentCreate } from "../../../../../../queries/document/use-patient-document-create";
import {
  UploadDocumentDocumentFormatEnum,
  UploadDocumentDocumentTagEnum,
} from "../../../../../../services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../../utils/utils";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useQueryClient } from "@tanstack/react-query";
import { getStoredRealm } from "@/utils/auth-storage";

const base64ToFile = (
  base64: string,
  filename: string,
  mimeType: string
): File => {
  const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;
  const byteString = atob(base64Data);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.codePointAt(i) ?? 0;
  }
  return new File([ab], filename, { type: mimeType });
};

interface SendToPatientModalProps {
  disclosureHost: DisclosureHost;
}

interface AddToPatientChartForm {
  patient: AsyncSelectOption | null;
  documentName: string;
  documentType: string;
  note: string;
}

const validationSchema = Yup.object({
  patient: Yup.object().nullable().required("Patient is required"),
  // documentName: Yup.string().trim().required("Document Name is required"),
  documentType: Yup.string().trim().required("Document Type is required"),
  note: Yup.string().trim().required("Document Name is required"),
});

const DOCUMENT_TAG_FAX = "FAX" as UploadDocumentDocumentTagEnum;

const SendToPatientModal = ({ disclosureHost }: SendToPatientModalProps) => {
  const faxData = disclosureHost.data;
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: createDocument } = usePatientDocumentCreate();

  const formik = useFormik<AddToPatientChartForm>({
    initialValues: {
      patient: null,
      documentName: faxData?.sFaxFileName || "Fax Document",
      documentType: "PDF",
      note: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const patientId = (values.patient as AsyncSelectOption)?.value;
      if (!patientId || !faxData?.result) return;
      const file = base64ToFile(
        faxData.result.startsWith("data:")
          ? faxData.result
          : `data:application/pdf;base64,${faxData.result}`,
        values.documentName?.trim() || "fax-document.pdf",
        "application/pdf"
      );
      const payload = {
        patientId,
        name: values.documentName?.trim() || "Fax Document",
        assigned: false,
        documentTag: DOCUMENT_TAG_FAX,
        documentFormat: UploadDocumentDocumentFormatEnum.Pdf,
        file,
        documentDescription: values.note?.trim() ?? undefined,
        xTENANTID: getStoredRealm() ?? undefined,
      };
      try {
        await createDocument(payload);
        showSnackbar(
          getSuccessSnackbar({ message: "Document added to patient chart" })
        );
        queryClient.invalidateQueries({ queryKey: ["patient-document-list"] });
        closeModal();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    enableReinitialize: true,
  });

  const closeModal = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={closeModal}
      dialogTitle="Add to Patient Chart"
      dialogAction={
        <Flex gap={2}>
          <Button
            variant="outlined"
            onClick={closeModal}
            sx={{
              borderColor: "#E0E0E0",
              color: "#333",
              textTransform: "none",
              minWidth: "80px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => formik.submitForm()}
            sx={{
              textTransform: "none",
              minWidth: "80px",
            }}
          >
            Done
          </Button>
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "600px",
          width: "100%",
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PatientSelectScroll
              label="Patient Name"
              required
              value={formik.values.patient}
              onChange={(value) => formik.setFieldValue("patient", value)}
              error={
                formik.touched.patient && formik.errors.patient
                  ? String(formik.errors.patient)
                  : false
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Document Name"
              required
              name="note"
              value={formik.values.note}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.note && !!formik.errors.note}
              helperText={formik.touched.note && formik.errors.note}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              disabled
              label="Document Type"
              required
              name="documentType"
              value={formik.values.documentType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.documentType && !!formik.errors.documentType
              }
              helperText={
                formik.touched.documentType && formik.errors.documentType
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {/* <Grid item xs={12}>
            <CustomTextArea
              label="Note"
              name="note"
              value={formik.values.note}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Note"
              minRows={3}
            />
          </Grid> */}
        </Grid>
      </form>
    </StyledDialog>
  );
};

export default SendToPatientModal;
